import {
  hasCompleteQueryData,
  throwOnQueryError,
} from 'ZalamoLibrary/src/lib/core/graphql/complete-query-data';
import { withApolloTypename } from 'ZalamoLibrary/src/lib/core/graphql/with-apollo-typename';

import {
  combineLatest,
  filter,
  first,
  map,
  pipe,
  switchMap,
  tap,
  timer,
} from 'rxjs';

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AliveState } from '../../../../core/ActiveState';

import {
  CommnetType,
  GetSupportStatusGQL,
  InsertSupportMessageGQL,
  InsertSupportMessageMutationVariables,
  StartSupportThreadGQL,
} from 'ZalamoLibrary/src/lib/ui/support/graphql';
import { OrganizationService } from 'ZalamoLibrary/src/lib/core/organizations/organization.service';
import {
  GetChatMessagesDocument,
  GetChatMessagesQuery,
  GetChatMessagesQueryVariables,
} from 'ZalamoLibrary/src/lib/ui/chat/graphql';

import cuid from 'cuid/index';
import { DateTime } from 'luxon';
import { UserDetailsService } from 'ZalamoLibrary/src/lib/zalamo/auth/user-details.service';
import { SupportMessageType } from '../../../../../../../zalamo-support/src/graphql';
import { MessageContentType } from 'ZalamoLibrary/src/lib/ui/chat/types';

const wideBreakpoint = '(max-width: 1500px)';

@Component({
  selector: 'lib-support-container',
  templateUrl: './support-container.component.html',
  styleUrls: ['./support-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SupportContainerComponent extends AliveState implements OnInit {
  @HostBinding('class.floating') isFloating =
    this.observer.isMatched(wideBreakpoint);
  @Output() closeMe = new EventEmitter();
  @Output() hideMe = new EventEmitter();
  supportStatus$ = timer(0, 60000).pipe(
    switchMap(() =>
      this.organizationService.getCurrentOrganization().pipe(
        switchMap((organizationId) =>
          this.getSupportStatusGQL
            .watch({
              variables: {
                organizationId,
              },
            })
            .valueChanges.pipe(
              throwOnQueryError,
              filter(hasCompleteQueryData),
              map((result) => result.data['getZalamoSupportThread']),
            ),
        ),
      ),
    ),
  );

  supportHeader$ = this.supportStatus$.pipe(
    map((supportStatus) => {
      if (supportStatus && supportStatus.currentSupportMember) {
        return `${supportStatus.currentSupportMember.name} - zespół Zalamo`;
      } else {
        return 'zespół Zalamo';
      }
    }),
  );
  chatAvatar$ = this.supportStatus$.pipe(
    filter((status) => status && !!status.currentSupportMember?.avatar),
    map(({ currentSupportMember: { avatar } }) => avatar),
  );

  private _isFaq = false;

  get isFaq() {
    return this._isFaq;
  }

  @Input() set isFaq(isFaq) {
    this._isFaq = isFaq;
    if (this.isFaq) {
      this._isChat = false;
    }

    console.log('IS FAQ', isFaq, this._isFaq, this.isFaq);
    this.cd.markForCheck();
  }

  private _isChat = false;

  get isChat() {
    return this._isChat;
  }

  @Input() set isChat(chat) {
    this._isChat = chat;
    if (this.isChat) {
      this._isFaq = false;
    }
  }

  constructor(
    private observer: BreakpointObserver,
    private cd: ChangeDetectorRef,
    private getSupportStatusGQL: GetSupportStatusGQL,
    private organizationService: OrganizationService,
    private startSupportThreadGQL: StartSupportThreadGQL,
    private insertSupportMessageGQL: InsertSupportMessageGQL,
    private userDetailsService: UserDetailsService,
  ) {
    super();
  }

  addMessage<K extends SupportMessageType>({
    message,
    type,
  }: {
    message: MessageContentType[K];
    type: CommnetType;
  }) {
    this.supportStatus$.pipe(first()).subscribe((supportStatus) => {
      if (!supportStatus || !supportStatus.conversationId) {
        this.organizationService
          .getCurrentOrganization()
          .pipe(
            switchMap((organizationId) =>
              this.startSupportThreadGQL.mutate({
                variables: {
                  message:
                    type === CommnetType.Text
                      ? (message as string)
                      : JSON.stringify(message),
                  organizationId,
                },
              }),
            ),
            switchMap(() =>
              this.organizationService.getCurrentOrganization().pipe(
                switchMap((organizationId) =>
                  this.getSupportStatusGQL.fetch({
                    ...{
                      fetchPolicy: 'network-only',
                    },
                    variables: {
                      organizationId,
                    },
                  }),
                ),
              ),
            ),
            first(),
          )
          .subscribe(() => {});
      } else {
        combineLatest([
          this.organizationService.getCurrentOrganization(),
          this.supportStatus$,
        ])
          .pipe(
            first(),
            switchMap(([organizationId, { conversationId }]) => {
              const messageContent =
                type === CommnetType.Text
                  ? (message as string)
                  : JSON.stringify(message);
              const input: InsertSupportMessageMutationVariables = {
                message: messageContent,
                organizationId,
                type,
                standaloneSupportMessageId: cuid(),
              };
              return this.insertSupportMessageGQL.mutate({
                ...{
                  optimisticResponse: withApolloTypename('Mutation', {
                    insertMessageForSupport: withApolloTypename(
                      'StandaloneSupportMessage',
                      {
                        standaloneSupportMessageId:
                          input.standaloneSupportMessageId,
                        date: DateTime.now().toSeconds(),
                        comment:
                          type === CommnetType.Text
                            ? (message as string)
                            : JSON.stringify(message),
                        type,
                        authorId: this.userDetailsService.getUserId(),
                      },
                    ),
                  }),
                  update: (cache, mutationResult) => {
                    const current = cache.readQuery<
                      GetChatMessagesQuery,
                      GetChatMessagesQueryVariables
                    >({
                      query: GetChatMessagesDocument,
                      variables: { organizationId, conversationId },
                    });

                    cache.writeQuery<
                      GetChatMessagesQuery,
                      GetChatMessagesQueryVariables
                    >({
                      query: GetChatMessagesDocument,
                      variables: { organizationId, conversationId },
                      data: {
                        ...current,
                        getSupportChatMessages: [
                          ...current.getSupportChatMessages,
                          mutationResult.data.insertMessageForSupport,
                        ],
                      },
                    });
                  },
                },
                variables: input,
              });
            }),
          )
          .subscribe();
      }
    });
  }

  ngOnInit(): void {
    this.subscribeWhileAlive(
      this.observer.observe(wideBreakpoint).pipe(
        tap((res) => {
          if (this.isFloating !== res.matches) {
            this.isFloating = res.matches;
          }
          this.cd.detectChanges();
        }),
      ),
    );
  }
}
